import { useEffect, useMemo, useState } from "react";
import { getAdminProfile } from "../../features/adminAuth/adminAuthStorage";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.verixxon.com";

const POST_TYPE_OPTIONS = [
  { value: "article", label: "Article" },
  { value: "newsletter", label: "Newsletter" },
  { value: "video", label: "Video" },
  { value: "update", label: "Platform Update" },
];

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
];

const INITIAL_FORM = {
  title: "",
  excerpt: "",
  content: "",
  postType: "article",
  status: "draft",
  coverImageUrl: "",
  videoUrl: "",
  newsletterUrl: "",
  tags: "",
};

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "N/A";
  return parsed.toLocaleString();
};

const parseJsonResponse = async (res) => {
  const text = await res.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error("Invalid server response");
  }
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data;
};

const BlogManagerPage = () => {
  const profile = getAdminProfile();

  const [posts, setPosts] = useState([]);
  const [totals, setTotals] = useState({
    total: 0,
    published: 0,
    draft: 0,
    videos: 0,
    newsletters: 0,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(INITIAL_FORM);
  const [editingId, setEditingId] = useState("");
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const loadPosts = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/blogs`);
      const data = await parseJsonResponse(res);
      setPosts(Array.isArray(data.data) ? data.data : []);
      setTotals(data.totals || {
        total: 0,
        published: 0,
        draft: 0,
        videos: 0,
        newsletters: 0,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
  }, []);

  const filteredPosts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return posts.filter((post) => {
      if (typeFilter !== "all" && post.postType !== typeFilter) {
        return false;
      }
      if (statusFilter !== "all" && post.status !== statusFilter) {
        return false;
      }
      if (!normalizedQuery) {
        return true;
      }

      const text = [post.title, post.excerpt, post.content, (post.tags || []).join(" ")]
        .join(" ")
        .toLowerCase();

      return text.includes(normalizedQuery);
    });
  }, [posts, query, typeFilter, statusFilter]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setForm(INITIAL_FORM);
    setEditingId("");
  };

  const startEdit = (post) => {
    setEditingId(post.id);
    setForm({
      title: post.title || "",
      excerpt: post.excerpt || "",
      content: post.content || "",
      postType: post.postType || "article",
      status: post.status || "draft",
      coverImageUrl: post.coverImageUrl || "",
      videoUrl: post.videoUrl || "",
      newsletterUrl: post.newsletterUrl || "",
      tags: Array.isArray(post.tags) ? post.tags.join(", ") : "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const endpoint = editingId
        ? `${API_BASE_URL}/api/admin/blogs/${editingId}`
        : `${API_BASE_URL}/api/admin/blogs`;
      const method = editingId ? "PUT" : "POST";
      const res = await fetch(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          authorEmail: profile?.email || "",
          authorName: profile?.name || "Admin",
        }),
      });
      await parseJsonResponse(res);
      await loadPosts();
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (post) => {
    const confirmed = window.confirm(`Delete "${post.title}"? This action cannot be undone.`);
    if (!confirmed) return;

    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/blogs/${post.id}`, {
        method: "DELETE",
      });
      await parseJsonResponse(res);
      await loadPosts();
      if (editingId === post.id) {
        resetForm();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <section className="admin-page-card blog-admin-page">
      <div className="blog-admin-header">
        <div>
          <h2>Blog CMS</h2>
          <p>
            Publish professional articles, newsletters, videos, and updates for your user-facing blog.
          </p>
        </div>
        <button
          type="button"
          className="blog-refresh-btn"
          onClick={loadPosts}
          disabled={loading}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div className="blog-summary-grid" aria-label="Blog summary">
        <article className="blog-summary-card">
          <span>Total</span>
          <strong>{totals.total}</strong>
        </article>
        <article className="blog-summary-card">
          <span>Published</span>
          <strong>{totals.published}</strong>
        </article>
        <article className="blog-summary-card">
          <span>Draft</span>
          <strong>{totals.draft}</strong>
        </article>
        <article className="blog-summary-card">
          <span>Videos</span>
          <strong>{totals.videos}</strong>
        </article>
        <article className="blog-summary-card">
          <span>Newsletters</span>
          <strong>{totals.newsletters}</strong>
        </article>
      </div>

      {error ? <p className="blog-admin-error">{error}</p> : null}

      <form className="blog-editor-form" onSubmit={handleSubmit}>
        <div className="blog-editor-grid">
          <label>
            Title
            <input
              type="text"
              value={form.title}
              onChange={(event) => handleChange("title", event.target.value)}
              placeholder="Enter post title"
              required
            />
          </label>
          <label>
            Type
            <select
              value={form.postType}
              onChange={(event) => handleChange("postType", event.target.value)}
            >
              {POST_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select
              value={form.status}
              onChange={(event) => handleChange("status", event.target.value)}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Cover Image URL
            <input
              type="url"
              value={form.coverImageUrl}
              onChange={(event) => handleChange("coverImageUrl", event.target.value)}
              placeholder="https://..."
            />
          </label>
          <label>
            Video URL
            <input
              type="url"
              value={form.videoUrl}
              onChange={(event) => handleChange("videoUrl", event.target.value)}
              placeholder="YouTube/Vimeo/video link"
            />
          </label>
          <label>
            Newsletter URL
            <input
              type="url"
              value={form.newsletterUrl}
              onChange={(event) => handleChange("newsletterUrl", event.target.value)}
              placeholder="External newsletter or PDF link"
            />
          </label>
          <label className="blog-editor-wide">
            Excerpt
            <textarea
              value={form.excerpt}
              onChange={(event) => handleChange("excerpt", event.target.value)}
              rows={3}
              placeholder="Short summary for listing cards"
            />
          </label>
          <label className="blog-editor-wide">
            Content
            <textarea
              value={form.content}
              onChange={(event) => handleChange("content", event.target.value)}
              rows={11}
              placeholder="Write the full post content"
              required
            />
          </label>
          <label className="blog-editor-wide">
            Tags (comma separated)
            <input
              type="text"
              value={form.tags}
              onChange={(event) => handleChange("tags", event.target.value)}
              placeholder="privacy, verification, guide"
            />
          </label>
        </div>

        <div className="blog-editor-actions">
          <button type="submit" className="blog-save-btn" disabled={saving}>
            {saving ? "Saving..." : editingId ? "Update Post" : "Create Post"}
          </button>
          <button type="button" className="blog-reset-btn" onClick={resetForm}>
            Clear
          </button>
        </div>
      </form>

      <div className="blog-list-toolbar">
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search title, excerpt, content, tags"
        />
        <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
          <option value="all">All types</option>
          {POST_TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          <option value="all">All status</option>
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="blog-post-grid">
        {filteredPosts.map((post) => (
          <article className="blog-admin-card" key={post.id}>
            <div className="blog-admin-card-head">
              <h3>{post.title}</h3>
              <div className="blog-admin-badges">
                <span className={`blog-badge blog-badge-type blog-badge-${post.postType}`}>
                  {post.postType}
                </span>
                <span className={`blog-badge ${post.status === "published" ? "blog-badge-published" : "blog-badge-draft"}`}>
                  {post.status}
                </span>
              </div>
            </div>
            <p className="blog-admin-card-excerpt">{post.excerpt || "No excerpt"}</p>
            <p className="blog-admin-card-meta">
              Updated: {formatDateTime(post.updatedAt)}
            </p>
            <div className="blog-admin-card-actions">
              <button type="button" onClick={() => startEdit(post)}>
                Edit
              </button>
              <button
                type="button"
                className="blog-delete-btn"
                onClick={() => handleDelete(post)}
              >
                Delete
              </button>
            </div>
          </article>
        ))}
      </div>

      {!loading && filteredPosts.length === 0 ? (
        <p className="blog-empty-note">No blog posts match your filters yet.</p>
      ) : null}
    </section>
  );
};

export default BlogManagerPage;
